import { message } from "antd";
import { useState, useEffect, useReducer } from "react";
import messages from "../../../../errors/messages.en-IN.json";
import useAuthStore from "../../../../stores/useAuthStore";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useCartStore from "../../../../stores/useCartStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import { roundTo, applyCurrencyRounding, getRoundingMethod, getPrecision } from "../../../../utils/tillHelpers";
import { enrichLineItemFields } from "../utils/taxCalculations";
import { normaliseApprover, storedApprover, applyApprovalRecord, removeRuleApproval } from "./discountApproval";

export const CheckoutTotalManualDiscount = async (
  pricingRule,
  setCart,
  cart,
  orderType,
  cartObj,
  discountInput,
  matchingApproval,
  iscoupon,
  couponInput,
  uniqReferenceId,
  mPricingCouponId,
  mPricingRulesId
) => {
  const isExchange = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
  // TIC-10890: approver from the approval drawer or the stored manualDiscountData entry (no PIN).
  const approver = normaliseApprover(matchingApproval);
  const saleTypeValidation = pricingRule.cwrSaletypeId === null ? true : pricingRule.cwrSaletypeId === orderType.cwrSaletype.cwrSaletypeId;
  const tillData = useAuthStore.getState().tillData;
  const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
  let taxIncludeFlag = saleTypeData?.customer?.istaxincluded === "Y" ? "Y" : tillData.tillAccess.csBunit.isTaxIncluded ? tillData.tillAccess.csBunit.isTaxIncluded : "Y";
  const billCart = { ...cartObj };
  const precision = tillData?.tillAccess?.csBunit?.currencies[0]?.prcPrecision || 2;
  // TIC-5459: a fixed-amount (discountType "V") Total Bill Discount is reapplied every time
  // removeAllDiscounts() replays persisted discounts (e.g. on customer attach/remove), or when
  // cart.js's addProductToCart replays every currently-active discount after a new product is
  // added. On reapply, item.nettotal/item.discount already reflect THIS SAME rule's prior
  // application — feeding them back in here double-subtracts the discount (once already baked
  // into nettotal, once again via the eligibility/base-amount math below) and can falsely trip
  // the "exceeds total bill" check or shrink the recomputed discount. The original fix only
  // detected this when the item carried exactly one rule (this one) — correct for that case, but
  // when 2+ different Total Bill Discount rules are stacked on the same item (the ManualDiscount
  // drawer supports this — see its own totalLevelDiscount[...length-1] prepopulate logic), item.discount
  // is the SUM of every rule's contribution, and reapplying the first-listed one double-counted
  // its own slice while leaving the other rules' amounts alone. getOwnPriorContribution tracks
  // each rule's own last-applied amount per item (appliedAmount, set below) so a reapply can
  // exclude exactly its own slice, regardless of how many other rules are also stacked — this is
  // a strict generalization of the original single-rule check (with appliedAmount unset, an item
  // carrying exactly one rule still resolves to excluding its full item.discount, identical to
  // the original behavior). Percentage ("P") discounts are left untouched — their math never
  // referenced item.discount and was already confirmed idempotent.
  const getOwnPriorContribution = (item) => {
    if (pricingRule.discountType !== "V") return 0;
    const entry = (item?.allDiscounts || []).find((d) => d.mPricingruleId === pricingRule.mPricingrulesId);
    if (!entry) return 0;
    // A line can never still carry more of this rule than its whole current discount. A quantity
    // edit resets item.discount to 0 before the rule is replayed; a recorded slice left behind by
    // such a reset must not be subtracted from a discount that no longer holds it (it made the
    // new share cancel out to 0 on a single line and go negative on a multi-line bill).
    if (typeof entry.appliedAmount === "number") return Math.min(entry.appliedAmount, Math.max(item.discount || 0, 0));
    // Backward-compat fallback for items saved before appliedAmount existed — only trust the
    // original "sole rule" heuristic, exactly the behavior TIC-5459 already shipped and confirmed.
    return item.allDiscounts.length === 1 ? item.discount : 0;
  };
  // BUG-2045: the pool-building pass (totalBillPrice, below) and the apply-time pass
  // (applyDiscountToItem, further below) must use the EXACT same priority-eligibility
  // formula -- if they drift, an item can count toward the pool but get rejected at
  // apply time (or vice versa), leaving a discount share undistributed and dumped onto
  // whichever item is last in the array. Shared here instead of duplicated so a future
  // change to either pass can't silently diverge from the other again.
  const isPriorityEligible = (entity) =>
    iscoupon
      ? entity?.priority
        ? entity?.priority < pricingRule.priority
        : true
      : entity?.priority
      ? entity?.priority <= pricingRule.priority || pricingRule.mPricingrulesId === entity.mPricingruleId
      : true;
  let nonReturnItemsTotal = 0;
  let nonReturnItemsQty = 0;
  let nonReturnItemsDiscount = 0;
  let nonReturnItems = [];
  billCart.items.map((item) => {
    if (!item.isReturn && item.isCanceled !== "Y") {
      const ownPriorContribution = getOwnPriorContribution(item);
      nonReturnItemsTotal += (item.nettotal || 0) + ownPriorContribution;
      nonReturnItemsQty += item.weight;
      nonReturnItemsDiscount += (item.discount || 0) - ownPriorContribution;
      nonReturnItems.push(item);
    }
  });

  let maxAmount = pricingRule.maxBillAmount ? Math.abs(nonReturnItemsTotal) <= pricingRule.maxBillAmount : true;
  const minQtyFlag = pricingRule.minimumQty ? Math.abs(nonReturnItemsQty) >= pricingRule.minimumQty : true;
  const maxQtyFlag = pricingRule.maximumQty ? Math.abs(nonReturnItemsQty) <= pricingRule.maximumQty : true;
  const minAmountFlag = pricingRule.billAmount ? Math.abs(nonReturnItemsTotal) >= pricingRule.billAmount : true;
  const maxAmountFlag = pricingRule.maxBillAmount ? Math.abs(nonReturnItemsTotal) <= pricingRule.maxBillAmount : true;
  let giftCardFlag = false;
  let uniqueFinalArray = [];

  // BUG-2045: this pool-building pass must mirror EVERY exclusion gate that
  // applyDiscountToItem's own excludingFlag computes below (layAway, category,
  // product, brand, B2C segment, B2C customer, business unit) — previously it
  // only checked layAway/category/product/brand, so an item restricted only by
  // B2C segment/customer/business-unit could count toward totalBillPrice here
  // yet be skipped entirely at apply time, leaving its "share" of the discount
  // undistributed and dumped onto the last-item remainder correction below.
  nonReturnItems.forEach((addedToCart) => {
    let excludingFlag = true;

    if (addedToCart?.layAway === "Y" && addedToCart?.weight <= 0) {
      excludingFlag = false;
    }

    if (pricingRule.mPricingB2CCustomerSegments.length > 0) {
      const matchingCustomerSegment = pricingRule.mPricingB2CCustomerSegments.find(
        (pc) => pc.cwrB2CCustomerSegmentId === cartObj.customer?.b2cCustomerSegment?.cwrB2CCustomerSegmentId
      );

      if (
        (pricingRule.excludeB2CSegment === "Y" && matchingCustomerSegment) ||
        (pricingRule.excludeB2CSegment !== "Y" && !matchingCustomerSegment && pricingRule.mPricingB2CCustomerSegments.length > 0) ||
        (pricingRule.excludeB2CSegment === "N" && pricingRule.mPricingB2CCustomerSegments.length === 0)
      ) {
        excludingFlag = false;
      }
    }

    if (pricingRule.mPricingB2CCustomers.length > 0) {
      const matchingCustomer = pricingRule.mPricingB2CCustomers.find((pc) => pc.b2cCustomerId === cartObj.customer?.cwrCustomerId);

      if (
        (pricingRule.excludeB2CCustomers === "Y" && matchingCustomer) ||
        (pricingRule.excludeB2CCustomers !== "Y" && !matchingCustomer && pricingRule.mPricingB2CCustomers.length > 0) ||
        (pricingRule.excludeB2CCustomers === "N" && pricingRule.mPricingB2CCustomers.length === 0)
      ) {
        excludingFlag = false;
      }
    }

    const matchingCategoy = pricingRule.mPricingPcategories.find((pc) => pc.mProductCategoryId === addedToCart.mProductCategoryId);

    if (
      (pricingRule.excludeProductCategories === "Y" && matchingCategoy) ||
      (pricingRule.excludeProductCategories !== "Y" && !matchingCategoy && pricingRule.mPricingPcategories.length > 0) ||
      (pricingRule.excludeProductCategories === "N" && pricingRule.mPricingPcategories.length === 0)
    ) {
      excludingFlag = false;
    }

    const matchingProduct = pricingRule.mPricingXProducts.find((pc) => pc.mProductId === addedToCart.productId);

    if (
      (pricingRule.excludeProducts === "Y" && matchingProduct) ||
      (pricingRule.excludeProducts !== "Y" && !matchingProduct && pricingRule.mPricingXProducts.length > 0) ||
      (pricingRule.excludeProducts === "N" && pricingRule.mPricingXProducts.length === 0)
    ) {
      excludingFlag = false;
    }

    const matchingBrand = pricingRule.mPricingBrands.find((pc) => pc.mBrandId === addedToCart.productBrandId);

    if (
      (pricingRule.excludeBrands === "Y" && matchingBrand) ||
      (pricingRule.excludeBrands !== "Y" && !matchingBrand && pricingRule.mPricingBrands.length > 0) ||
      (pricingRule.excludeBrands === "N" && pricingRule.mPricingBrands.length === 0)
    ) {
      excludingFlag = false;
    }

    const matchingBusinessUnit = pricingRule.mPricingBUnits.find((pc) => pc.mBunitPricingId === tillData.tillAccess.csBunit.csBunitId);
    if (
      (pricingRule.excludeBusinessUnits === "Y" && matchingBusinessUnit) ||
      (pricingRule.excludeBusinessUnits !== "Y" && !matchingBusinessUnit && pricingRule.mPricingBUnits.length > 0) ||
      (pricingRule.excludeBusinessUnits === "N" && pricingRule.mPricingBUnits.length === 0)
    ) {
      excludingFlag = false;
    }
    if (excludingFlag) uniqueFinalArray.push(addedToCart);
  });

  let totalBillPrice = 0;
  let validBillAmount = 0;
  uniqueFinalArray.map((product) => {
    // BUG-2045: must use the exact same eligibility formula as applyDiscountToItem's
    // priorityEligible check below (see isPriorityEligible's own comment) — previously
    // this pool pass always used the non-coupon "<=" branch regardless of iscoupon, so
    // for a coupon-driven rule an item with priority === pricingRule.priority (owned by
    // a different rule) counted toward totalBillPrice here but was then rejected by the
    // stricter "<" apply-time check, leaving its share of the discount undistributed
    // and dumped onto the remainder correction below.
    if (isPriorityEligible(product)) {
      const productDiscountForBase = (product.discount || 0) - getOwnPriorContribution(product);
      totalBillPrice = totalBillPrice + parseFloat(product.realPrice * Math.abs(product.weight) - productDiscountForBase);
    }
    validBillAmount = validBillAmount + parseFloat(product.realPrice * Math.abs(product.weight));
  });

  if (totalBillPrice - discountInput < 0 && pricingRule.discountType === "V" && billCart.items.length > 0) {
    message.error(`${messages.errors.discounts.amount_exceeds_total}`);
    return { ...cartObj, discountApplied: false };
  }
  let discountedAmount = ((totalBillPrice + cart.discount) * discountInput) / 100;
  // BUG-1990 (part 2): this compared cart.total (already currency-rounded via
  // applyCurrencyRounding, e.g. RN to the nearest rupee) against discountedAmount
  // (derived from totalBillPrice, which is raw/unrounded). For fractional-quantity
  // items the two can disagree by a few paise once rounding shaves the total down —
  // e.g. product 10021 "RASBHARI" at qty 0.12 x realPrice 720 = 86.40, rounded to
  // cart.total=86 under GPR's RN rounding — which made a legitimate 100% discount
  // look like it "exceeds total" and silently rejected it before it ever applied.
  // Compare against totalBillPrice instead — the same raw value discountedAmount was
  // derived from — so both sides of the inequality are on equal footing, matching
  // the discountType "V" check three lines above (which already correctly uses
  // totalBillPrice, not cart.total). Algebraically this reduces to
  // (totalBillPrice + cart.discount) * (1 - discountInput/100) < 0, which is only
  // negative when the base itself is already negative or discountInput exceeds
  // 100% — never due to currency rounding. It can still land a hair below zero
  // from plain IEEE-754 float error though (e.g. 720 * 0.12 = 86.39999999999999,
  // not exactly 86.4) once run through the +/-/* chain above, so round to the
  // till's currency precision before comparing — same idiom cart.js already uses
  // (Math.round((x + Number.EPSILON) * factor) / factor) to strip that noise.
  const percentDiscountGuardValue = parseFloat((totalBillPrice + cart.discount - discountedAmount).toFixed(precision));
  if (percentDiscountGuardValue < 0 && pricingRule.discountType === "P" && billCart.items.length > 0) {
    message.error(`${messages.errors.discounts.amount_exceeds_total}`);
    return { ...cartObj, discountApplied: false };
  }

  const actualDiscountAmount = pricingRule.discountType === "P" ? (discountInput / 100) * validBillAmount : discountInput;
  if (actualDiscountAmount > validBillAmount && billCart.items.length > 0) {
    message.error(`${messages.errors.discounts.amount_exceeds_total}`);
    return { ...cartObj, discountApplied: false };
  }

  const gateBillAmountFloor = Math.abs(nonReturnItemsTotal) + Math.abs(nonReturnItemsDiscount) >= pricingRule.billAmount;
  const gateValidBillAmountFloor = validBillAmount >= pricingRule.billAmount;
  const gateDiscountWithinBill = actualDiscountAmount <= validBillAmount;
  const gatePassed =
    gateBillAmountFloor && maxAmount && minQtyFlag && maxQtyFlag && minAmountFlag && maxAmountFlag && gateValidBillAmountFloor && !giftCardFlag && gateDiscountWithinBill;

  if (gatePassed) {
    let discountValue = 0;
    discountValue = pricingRule.discountType === "V" ? discountInput : (discountInput / 100) * nonReturnItemsTotal;
    let discountType = pricingRule.discountType === "V" ? "FD" : "PD";
    if (pricingRule.issueGiftVoucher === "N" || pricingRule.issueGiftVoucher === null) {
      const cartItemsFiltered = billCart.items.filter((i) => i.nextRule === "Y" || i.nextRule === undefined);
      let salePricesTotal = 0;
      for (let i = 0; i < cartItemsFiltered.length; i += 1) {
        salePricesTotal += parseFloat(cartItemsFiltered[i].realPrice * cartItemsFiltered[i].weight);
      }
      let discountFlag = false;
      let totalAppliedDiscount = 0;

      // Helper function to apply discount to an item (parent or child)
      const applyDiscountToItem = (item, index, itemsArray, parentDiscount = null, childrenTotalPrice = null) => {
        if (item.isCanceled === "Y") return;
        let excludingFlag = true;

        if (item?.layAway === "Y" && item?.weight <= 0) {
          excludingFlag = false;
        }
        if (pricingRule.mPricingB2CCustomerSegments.length > 0) {
          const matchingCustomerSegment = pricingRule.mPricingB2CCustomerSegments.find(
            (pc) => pc.cwrB2CCustomerSegmentId === cartObj.customer?.b2cCustomerSegment?.cwrB2CCustomerSegmentId
          );

          if (
            (pricingRule.excludeB2CSegment === "Y" && matchingCustomerSegment) ||
            (pricingRule.excludeB2CSegment !== "Y" && !matchingCustomerSegment && pricingRule.mPricingB2CCustomerSegments.length > 0) ||
            (pricingRule.excludeB2CSegment === "N" && pricingRule.mPricingB2CCustomerSegments.length === 0)
          ) {
            excludingFlag = false;
          }
        }

        if (pricingRule.mPricingB2CCustomers.length > 0) {
          // Live-reported (GPR): after attach-then-remove-customer, the Total Bill Discount silently
          // vanished from the cart with no error shown. Root cause -- cartObj.customer was accessed
          // without optional chaining here, unlike every other customer reference in this file
          // (lines 113/126/291 all use cartObj.customer?.x). During the attach/remove transition
          // cartObj.customer can genuinely be null/undefined for one pass, and this line threw --
          // applyDiscountToItem is called synchronously from billCart.items.forEach, so the throw
          // propagated out of CheckoutTotalManualDiscount entirely. Its only caller during a
          // customer attach/remove (removeAllDiscounts's discount-replay loop, useCartOperations.js)
          // wraps the call in try/catch with just a console.error -- so the replay silently failed,
          // leaving every item's discount at the 0 that calculateCartItem had already reset it to
          // moments earlier in that same removeAllDiscounts call, with no user-facing error at all.
          const matchingCustomer = pricingRule.mPricingB2CCustomers.find((pc) => pc.b2cCustomerId === cartObj.customer?.cwrCustomerId);

          if (
            (pricingRule.excludeB2CCustomers === "Y" && matchingCustomer) ||
            (pricingRule.excludeB2CCustomers !== "Y" && !matchingCustomer && pricingRule.mPricingB2CCustomers.length > 0) ||
            (pricingRule.excludeB2CCustomers === "N" && pricingRule.mPricingB2CCustomers.length === 0)
          ) {
            excludingFlag = false;
          }
        }

        const matchingCategoy = pricingRule.mPricingPcategories.find((pc) => pc.mProductCategoryId === item.mProductCategoryId);

        if (
          (pricingRule.excludeProductCategories === "Y" && matchingCategoy) ||
          (pricingRule.excludeProductCategories !== "Y" && !matchingCategoy && pricingRule.mPricingPcategories.length > 0) ||
          (pricingRule.excludeProductCategories === "N" && pricingRule.mPricingPcategories.length === 0)
        ) {
          excludingFlag = false;
        }

        const matchingProduct = pricingRule.mPricingXProducts.find((pc) => pc.mProductId === item.productId);

        if (
          (pricingRule.excludeProducts === "Y" && matchingProduct) ||
          (pricingRule.excludeProducts !== "Y" && !matchingProduct && pricingRule.mPricingXProducts.length > 0) ||
          (pricingRule.excludeProducts === "N" && pricingRule.mPricingXProducts.length === 0)
        ) {
          excludingFlag = false;
        }

        const matchingBrand = pricingRule.mPricingBrands.find((pc) => pc.mBrandId === item.productBrandId);

        if (
          (pricingRule.excludeBrands === "Y" && matchingBrand) ||
          (pricingRule.excludeBrands !== "Y" && !matchingBrand && pricingRule.mPricingBrands.length > 0) ||
          (pricingRule.excludeBrands === "N" && pricingRule.mPricingBrands.length === 0)
        ) {
          excludingFlag = false;
        }

        const matchingBusinessUnit = pricingRule.mPricingBUnits.find((pc) => pc.mBunitPricingId === tillData.tillAccess.csBunit.csBunitId);
        if (
          (pricingRule.excludeBusinessUnits === "Y" && matchingBusinessUnit) ||
          (pricingRule.excludeBusinessUnits !== "Y" && !matchingBusinessUnit && pricingRule.mPricingBUnits.length > 0) ||
          (pricingRule.excludeBusinessUnits === "N" && pricingRule.mPricingBUnits.length === 0)
        ) {
          excludingFlag = false;
        }

        const matchingProductIndex = pricingRule.mPricingXProducts.findIndex((op) => op.mProductId === item.productId);

        if (
          (pricingRule.excludeProducts === "Y" && matchingProductIndex !== -1) ||
          (pricingRule.excludeProducts !== "Y" && matchingProductIndex === -1 && pricingRule.mPricingXProducts.length > 0) ||
          (pricingRule.excludeProducts === "N" && pricingRule.mPricingXProducts.length === 0)
        ) {
          excludingFlag = false;
        }

        let pricingRuleFlag = true;

        if (item.discount <= 0) {
          const matchingProduct = pricingRule.mPricingXProducts.find((res) => res.mProductId === item.productId);
          if (matchingProduct) {
            delete item.priority;
          }
        }

        // BUG-1936: this item's actual participation must match the eligibility gate that built
        // totalBillPrice (the discount pool basis) above — otherwise an item excluded from the pool
        // (because a higher/equal-priority rule already owns it) still consumes a share computed
        // against that smaller pool, and the discountValue-remainder correction (below) then dumps
        // the resulting oversized mismatch onto whichever item is last in the array, silently
        // wiping out or distorting its discount. Uses the same isPriorityEligible helper as the
        // pool-building pass above (see BUG-2045 comment there).
        if (pricingRuleFlag && excludingFlag && saleTypeValidation && !item.isReturn && isPriorityEligible(item)) {
          let allDiscounts = item?.allDiscounts || [];
          let approvalData = item?.approval || [];
          let matchedRuleIndex = allDiscounts.findIndex((d) => d.mPricingruleId === pricingRule.mPricingrulesId);

          let discountAmt;
          let sp;
          // Set only in the FD parent branch below — this rule's own final per-item contribution,
          // recorded onto allDiscounts so a future reapply can exclude exactly this amount.
          let thisRuleAppliedAmount = 0;
          if (parentDiscount !== null && childrenTotalPrice !== null) {
            // Child item: Distribute parent's discount proportionally
            if (discountType === "FD") {
              const childPrice = parseFloat(item.realPrice * item.weight);
              discountAmt = (childPrice / childrenTotalPrice) * parentDiscount;
              const taxInclude = (item.realPrice * item.taxRate) / 100;
              const childGross = (taxIncludeFlag === "Y" ? item.realPrice : item.realPrice + taxInclude) * item.weight;
              // TSK-3145: cap at the child's own gross so a proportional slice of an
              // oversized/compounded parentDiscount can't push this child's net negative.
              // TIC-9783: same fix as the parent FD branch — never fall back to item.nettotal.
              if (!isFinite(childGross) || childGross < 0) {
                console.warn(`[CheckoutTotalManualDiscount] Skipping child item ${item.productId} — realPrice unavailable (childGross=${childGross}), refusing to reapply discount against nettotal.`);
                return;
              }
              item.discount = childGross >= 0 && discountAmt > childGross ? childGross : discountAmt;
              sp = childGross - item.discount;
              const tax = sp - sp / (1 + item.taxRate / 100);
              // The parent's share is already in totalAppliedDiscount (FD parent branch). The
              // children only split that same amount, so they are not added again here; any
              // difference a child cap makes is added after the children are summed below.
              item.taxAmount = parseFloat(tax.toFixed(precision));
              item.nettotal = parseFloat(sp.toFixed(precision));
            } else {
              const childTotal = parseFloat(item.realPrice) * item.weight;
              let discountAmt = (discountInput / 100) * childTotal;
              // TSK-3145: cap at the child's own pre-tax total so a misconfigured/oversized
              // percentage input can't push priceAfterDiscount negative.
              discountAmt = childTotal >= 0 && discountAmt > childTotal ? childTotal : discountAmt;
              item.discount = parseFloat(discountAmt.toFixed(precision));

              const priceAfterDiscount = childTotal - item.discount;
              let tax = 0;
              if (taxIncludeFlag === "Y") {
                tax = priceAfterDiscount - priceAfterDiscount / (1 + item.taxRate / 100);
                item.nettotal = parseFloat(priceAfterDiscount.toFixed(precision));
              } else {
                tax = (priceAfterDiscount * item.taxRate) / 100;
                item.nettotal = parseFloat((priceAfterDiscount + tax).toFixed(precision));
              }

              totalAppliedDiscount += item.discount;
              item.taxAmount = parseFloat(tax.toFixed(precision));
              // BUG-2629: sp was never set on this branch, so item.salePrice below became
              // undefined and enrichLineItemFields (tax-exclusive) stamped netunit/grossunit 0
              // on every Custom Mix child -- retrieval then showed the child Price as 0. Same
              // net unit price the other PricingRules children loops use.
              sp = item.weight !== 0 ? (item.nettotal - item.taxAmount) / item.weight : 0;
            }
          } else if (discountType === "FD") {
            // Parent item with fixed discount
            // TIC-5459 (generalized, see getOwnPriorContribution above): the base for this rule's
            // own share must exclude every OTHER stacked rule's contribution (otherRulesContribution)
            // — not this rule's own prior slice. A fresh apply has otherRulesContribution ===
            // item.discount (own slice is 0), matching the original fresh-apply formula exactly. A
            // sole reapply has otherRulesContribution === 0 (own slice is the whole thing), matching
            // TIC-5459's original "treat as fresh, base=0" behavior exactly. A reapply while another
            // rule is also stacked correctly keeps only that other rule's amount as the base.
            const ownPriorContribution = getOwnPriorContribution(item);
            const otherRulesContribution = (item.discount || 0) - ownPriorContribution;
            discountAmt = parseFloat((discountInput * ((parseFloat(item.realPrice * item.weight) - otherRulesContribution) / totalBillPrice)).toFixed(precision));
            const rawItemDiscount = discountAmt + otherRulesContribution;
            const taxInclude = (item.realPrice * item.taxRate) / 100;
            const itemGross = (taxIncludeFlag === "Y" ? item.realPrice : item.realPrice + taxInclude) * item.weight;
            // TSK-3145: independent backstop on top of the TIC-5459 guard above — if that guard's
            // reapply detection is ever fooled (stale allDiscounts snapshot, a second apply before
            // the first settled, etc.) the cumulative discountAmt + otherRulesContribution can exceed
            // the item's own gross and drive nettotal deeply negative (confirmed on JH/26-27/2843:
            // 154315 + 154315 = 308630 against a 171465 gross). Cap at the item's own gross so this
            // can never happen regardless of how it got here.
            // TIC-9783: itemGross must come from item.realPrice, never item.nettotal — nettotal may
            // already be net-of-discount from a prior application. Falling back to it here caused a
            // real order's line total to be computed as (gross - discount) - discount again, landing
            // deeply negative (confirmed: 7195 - 2*3600 = -5) while the header stayed correct — the
            // backend rejected the order with a header/line mismatch it could never self-heal.
            // itemGross === 0 (a genuine free/zero-priced item) is left to the clamp below, unchanged.
            // A Custom Mix / grouped PARENT is exempt: unlike a real product, its own realPrice is
            // never populated (price lives entirely on its children) — itemGross is legitimately
            // NaN here on every application, not a corruption signal. Its nettotal/discount get
            // overwritten by the children-aggregation pass right after this forEach entry anyway
            // (see below), so this call is a structural no-op for a grouped parent either way; the
            // guard would otherwise skip it every single time and log a false-positive warning on
            // every Custom Mix order. Live-confirmed: item F65D95AE701B451EBA65BBA0C744DCF7 (a
            // Custom Mix parent) tripped this guard and the discount silently failed to distribute.
            const isGroupedParent = Array.isArray(item.children) && item.children.length > 0;
            if (!isGroupedParent && (!isFinite(itemGross) || itemGross < 0)) {
              console.warn(`[CheckoutTotalManualDiscount] Skipping item ${item.productId} — realPrice unavailable (itemGross=${itemGross}), refusing to reapply discount against nettotal.`);
              return;
            }
            item.discount = itemGross >= 0 && rawItemDiscount > itemGross ? itemGross : rawItemDiscount;
            sp = isGroupedParent ? (itemGross || item.nettotal) - item.discount : itemGross - item.discount;
            const tax = sp - sp / (1 + item.taxRate / 100);
            // This rule's own final contribution (after any TSK-3145 clamping) — used both to
            // record onto its allDiscounts entry (so the NEXT reapply can exclude exactly this
            // amount, not the full cumulative item.discount) and for the discountValue-remainder
            // correction below, which must track only THIS rule's own total distributed so far —
            // accumulating the full cumulative item.discount there (as the pre-existing code did)
            // double-counts every OTHER stacked rule's amount into the remainder math too, producing
            // a large negative "remainder" that got subtracted back off the last item, silently
            // erasing most of this rule's own discount the moment a second rule was stacked.
            thisRuleAppliedAmount = item.discount - otherRulesContribution;
            totalAppliedDiscount += thisRuleAppliedAmount;
            item.taxAmount = parseFloat(tax.toFixed(precision));
            item.nettotal = parseFloat(sp.toFixed(precision));
          } else {
            // Parent item with percentage discount
            const realPriceNum = parseFloat(item.realPrice);
            discountAmt = (discountInput / 100) * realPriceNum;
            // TSK-3145: cap at the item's own per-unit realPrice so a misconfigured/oversized
            // percentage input can't push sp/nettotal negative.
            discountAmt = realPriceNum >= 0 && discountAmt > realPriceNum ? realPriceNum : discountAmt;
            item.discount = parseFloat((discountAmt * item.weight).toFixed(precision));
            sp = realPriceNum - discountAmt;
            const mrp = parseFloat(sp) * item.weight;
            const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + item.taxRate / 100) : (mrp * item.taxRate) / 100;
            totalAppliedDiscount += item.discount;
            item.taxAmount = parseFloat(tax.toFixed(precision));
            item.nettotal = parseFloat((taxIncludeFlag === "Y" ? mrp : mrp + tax).toFixed(precision));
          }
          // appliedAmount is only meaningful for genuine parent items — getOwnPriorContribution is
          // never consulted for children (they're proportionally re-derived from the parent's own,
          // already-corrected discount each time instead), so leave their entries untouched.
          const isParentItem = parentDiscount === null && childrenTotalPrice === null;
          if (matchedRuleIndex === -1) {
            allDiscounts.push({
              mPricingruleId: pricingRule.mPricingrulesId,
              discountValue: discountValue,
              discountType: discountType,
              ...(isParentItem && { appliedAmount: thisRuleAppliedAmount }),
            });
          } else if (isParentItem && discountType === "FD") {
            // Keep this rule's own recorded contribution current — the NEXT reapply (of this or
            // any other stacked rule) depends on it to exclude exactly this rule's slice.
            allDiscounts[matchedRuleIndex] = {
              ...allDiscounts[matchedRuleIndex],
              discountValue: discountValue,
              appliedAmount: thisRuleAppliedAmount,
            };
          }
          // TIC-10890: write/replace/remove this rule's approval record (see discountApproval.js).
          approvalData = applyApprovalRecord(approvalData, pricingRule.mPricingrulesId, approver);
          discountFlag = true;
          item.salePrice = sp;
          item.discountName = pricingRule.printedName;
          item.mPricingruleId = pricingRule.mPricingrulesId;
          item.iscoupon = iscoupon;
          item.couponInput = couponInput;
          item.referenceId = uniqReferenceId;
          item.mPricingCouponId = mPricingCouponId;
          item.priority = pricingRule.priority;
          item.allDiscounts = allDiscounts;
          item.nextRule = pricingRule.nextRule;
          item.approval = approvalData;
          enrichLineItemFields(item, taxIncludeFlag, precision);
          itemsArray[index] = item;
        }
      };

      // Process each item and its children
      billCart.items.forEach((addedToCart, index) => {
        // Apply discount to the parent item
        applyDiscountToItem(addedToCart, index, billCart.items);

        // Process children if they exist
        if (addedToCart.children && Array.isArray(addedToCart.children)) {
          // Calculate total price of children before discount
          const childrenTotalPrice = addedToCart.children.reduce((sum, child) => sum + parseFloat(child.realPrice * child.weight), 0);

          // Distribute parent's discount to children
          addedToCart.children.forEach((child, childIndex) => {
            applyDiscountToItem(child, childIndex, addedToCart.children, addedToCart.discount, childrenTotalPrice);
          });

          // Calculate parent's nettotal as sum of its own nettotal and children's nettotal
          let childrenNetTotal = addedToCart.children.reduce((sum, child) => sum + (child.nettotal || 0), 0);
          addedToCart.nettotal = childrenNetTotal;

          let childrenDiscount = addedToCart.children.reduce((sum, child) => sum + (child.discount || 0), 0);
          if (discountType === "FD") {
            // Count a Custom Mix once: only what the children hold over or under the parent's share.
            totalAppliedDiscount += childrenDiscount - (addedToCart.discount || 0);
          }
          addedToCart.discount = childrenDiscount;

          let childrenTaxAmount = addedToCart.children.reduce((sum, child) => sum + (child.taxAmount || 0), 0);
          addedToCart.taxAmount = childrenTaxAmount;
          enrichLineItemFields(addedToCart, taxIncludeFlag, precision);
        }
      });

      // Last-item remainder correction: rounding each item's proportional discount
      // can cause the sum to be ±0.01 off from discountValue. Give the remainder
      // to the last eligible item so the total always equals discountInput exactly.
      if (discountType === "FD" && discountFlag) {
        let discountRemainder = parseFloat((discountValue - totalAppliedDiscount).toFixed(precision));
        if (discountRemainder > 0) {
          // BUG-2045: this used to add the ENTIRE remainder onto a single item's
          // discount with no gross cap — if the remainder ever exceeds that item's
          // own gross (e.g. from the pool/eligibility mismatch fixed above, or any
          // other source of under-distribution), its net total went negative
          // (confirmed repro: a ₹100 item absorbing a ₹900 remainder -> net -₹800).
          // Mirror the same itemGross clamp the FD parent branch already applies
          // (TSK-3145 above) and, if one item can't fully absorb the remainder,
          // spread the leftover across the next eligible items instead of ever
          // pushing a single line's net total negative.
          for (let i = billCart.items.length - 1; i >= 0 && discountRemainder > 0; i--) {
            const candidate = billCart.items[i];
            if (candidate.isCanceled !== "Y" && !candidate.isReturn && candidate.mPricingruleId === pricingRule.mPricingrulesId && !(candidate.children?.length > 0)) {
              const taxInclude = (candidate.realPrice * candidate.taxRate) / 100;
              const itemGross = (taxIncludeFlag === "Y" ? candidate.realPrice : candidate.realPrice + taxInclude) * candidate.weight;
              const before = candidate.discount || 0;
              if (itemGross <= before) continue; // no real headroom left on this item
              const rawNewDiscount = before + discountRemainder;
              // Mirror the FD parent branch's own clamp exactly (raw itemGross, not
              // toFixed) so a candidate that lands exactly on its own gross is never
              // pushed a hair past it by rounding — same idiom as TSK-3145 above.
              if (itemGross >= 0 && rawNewDiscount > itemGross) {
                candidate.discount = itemGross;
                discountRemainder = parseFloat((discountRemainder - (itemGross - before)).toFixed(precision));
              } else {
                candidate.discount = parseFloat(rawNewDiscount.toFixed(precision));
                discountRemainder = 0;
              }
              const sp = itemGross - candidate.discount;
              const tax = sp - sp / (1 + candidate.taxRate / 100);
              candidate.taxAmount = parseFloat(tax.toFixed(precision));
              candidate.nettotal = parseFloat(sp.toFixed(precision));
              enrichLineItemFields(candidate, taxIncludeFlag, precision);
            }
          }
          // If no eligible item had enough headroom to absorb the full remainder,
          // a small residual mismatch is left between discountValue and
          // totalAppliedDiscount rather than ever forcing a negative net total.
        } else if (discountRemainder < 0) {
          // Take the excess back from the last lines, never below 0 on any line: a line that
          // cannot give it all back passes the rest to the line before it.
          for (let i = billCart.items.length - 1; i >= 0 && discountRemainder < 0; i--) {
            const lastItem = billCart.items[i];
            if (lastItem.isCanceled !== "Y" && !lastItem.isReturn && lastItem.mPricingruleId === pricingRule.mPricingrulesId && !(lastItem.children?.length > 0)) {
              const before = lastItem.discount || 0;
              if (before <= 0) continue;
              const takeBack = Math.min(before, -discountRemainder);
              lastItem.discount = parseFloat((before - takeBack).toFixed(precision));
              discountRemainder = parseFloat((discountRemainder + takeBack).toFixed(precision));
              const taxInclude = (lastItem.realPrice * lastItem.taxRate) / 100;
              const sp = ((taxIncludeFlag === "Y" ? lastItem.realPrice : lastItem.realPrice + taxInclude) * lastItem.weight) - lastItem.discount;
              const tax = sp - sp / (1 + lastItem.taxRate / 100);
              lastItem.taxAmount = parseFloat(tax.toFixed(precision));
              lastItem.nettotal = parseFloat(sp.toFixed(precision));
              enrichLineItemFields(lastItem, taxIncludeFlag, precision);
            }
          }
        }
      }

      billCart.mPricingruleId = pricingRule.mPricingrulesId;
      billCart.billDiscount = billCart.billDiscount ? billCart.billDiscount + discountValue : discountValue;

      const addedToCart = billCart.items;

      let totalTax = 0;
      let totalPrice = 0;
      let totalItemsQty = 0;
      let totalDiscounts = 0;

      // Calculate totals — parent nettotal/taxAmount/discount already include children (set above)
      for (let i = 0; i < addedToCart.length; i += 1) {
        if (addedToCart[i].isCanceled === "Y") continue;
        totalPrice += addedToCart[i].nettotal;
        totalItemsQty += addedToCart[i].weight;
        totalTax += addedToCart[i].taxAmount;
        totalDiscounts += addedToCart[i].discount;
        addedToCart[i].key = i;
      }
      const { roundedTotal: roundOffValue, roundOffAmount: totalRoundOff } = applyCurrencyRounding(totalPrice, getRoundingMethod(tillData), getPrecision(tillData));
      let couponsData = cart?.couponInput?.length > 0 ? [...cart.couponInput] : [];

      if (!couponsData.some((coupon) => coupon.couponCode === couponInput) && couponInput !== undefined && couponInput !== null && discountFlag) {
        couponsData.push({ couponCode: couponInput, referenceId: uniqReferenceId, mPricingCouponId: mPricingCouponId, mPricingruleId: pricingRule.mPricingrulesId });
      }

      let uniqueArray = cart?.manualDiscountData?.totalLevelDiscount || [];
      let lineLevelDiscounts = cart?.manualDiscountData?.lineLevelDiscount || [];
      let totalLevelDiscounts = cart?.manualDiscountData?.totalLevelDiscount || [];

      let allSequenceNos = [...lineLevelDiscounts.map((item) => item.sequenceNo), ...totalLevelDiscounts.map((item) => item.sequenceNo)];

      let nextSequenceNo = 1;
      while (allSequenceNos.includes(nextSequenceNo)) {
        nextSequenceNo++;
      }

      let duplicateIndex = uniqueArray.findIndex((item) => item.pricingRule === pricingRule.mPricingrulesId);
      const itemData = {
        pricingRule: pricingRule.mPricingrulesId,
        discountName: pricingRule.printedName,
        discountValue: discountInput,
        sequenceNo: duplicateIndex === -1 ? nextSequenceNo : totalLevelDiscounts[duplicateIndex]?.sequenceNo,
        // TIC-10890: kept here so every re-apply passes the same approver back in.
        approver: storedApprover(approver, totalLevelDiscounts[duplicateIndex]),
      };
      if (duplicateIndex === -1) {
        uniqueArray.push(itemData);
      } else {
        uniqueArray[duplicateIndex] = itemData;
      }

      let manualDiscountData = {
        totalLevelDiscount: uniqueArray,
        ...cart.manualDiscountData,
      };
      let obj = {
        ...cartObj,
        items: [...addedToCart],
        total: roundOffValue,
        tax: roundTo(totalTax, getPrecision(tillData)),
        // BUG-1990: this rule (e.g. a 100% "Complimentary Discount") folds its
        // discount into each item's nettotal and rounds totalPrice into `total`
        // above, but historically left `subTotal` untouched, still carrying the
        // pre-discount value. useAdditionalCharges.js's reactive recompute
        // (fires on every cart.items change, i.e. immediately after this runs)
        // rebuilds total from subTotal + tax + charges, so a stale subTotal
        // silently reverted a discounted (e.g. ₹0) total back toward the
        // pre-discount amount and forced a bogus round-off on top of it —
        // "round-off amount is not being accepted" for Complimentary sales.
        subTotal: parseFloat(parseFloat(totalPrice - totalTax).toFixed(precision)),
        discount: totalDiscounts,
        totalQty: totalItemsQty,
        roundOff: totalRoundOff,
        iscoupon: iscoupon ? iscoupon : false,
        couponInput: couponsData,
        totalBillDicount: iscoupon ? pricingRule.mPricingrulesId : false,
        totalDiscountFlag: true,
        manualDiscountData: manualDiscountData,
        discountApplied: true,
      };
      if (isExchange) {
        useCartStore.getState().setExchangeCart(obj);
      } else if (!iscoupon && !isExchange) {
        setCart(obj);
      }
      return obj;
    } else {
      let couponsData = cart.couponInput ? [...cart.couponInput] : [];
      if (!couponsData.some((coupon) => coupon.couponCode === couponInput) && couponInput !== undefined && couponInput !== null) {
        couponsData.push({ couponCode: couponInput, referenceId: uniqReferenceId, mPricingCouponId: mPricingCouponId, mPricingruleId: pricingRule.mPricingrulesId });
      }
      billCart.giftVoucherType = pricingRule.giftVoucherType;
      billCart.giftVoucherAmount = discountValue;
      billCart.iscoupon = cart.iscoupon ? cart.iscoupon : false;
      billCart.couponInput = couponsData;
      billCart.totalBillDicount = !iscoupon ? null : pricingRule.mPricingrulesId;
      if (isExchange) {
        useCartStore.getState().setExchangeCart({ ...billCart });
      } else {
        setCart({ ...billCart });
      }
      return { ...billCart };
    }
  } else {
    // Surface why the discount didn't apply — previously the drawer closed
    // silently and the user thought "Apply Discount" was broken. The most
    // common gates are minimum bill amount and category/brand exclusions.
    // (Issue #8)
    const reasons = [];
    if (pricingRule.billAmount && validBillAmount < pricingRule.billAmount) {
      reasons.push(`bill must be at least ${pricingRule.billAmount}`);
    }
    if (uniqueFinalArray.length === 0 && billCart.items.length > 0) {
      reasons.push("no eligible items match this rule");
    }
    if (pricingRule.maxBillAmount && Math.abs(nonReturnItemsTotal) > pricingRule.maxBillAmount) {
      reasons.push(`bill exceeds maximum ${pricingRule.maxBillAmount}`);
    }
    if (actualDiscountAmount > validBillAmount) {
      reasons.push("discount exceeds eligible bill amount");
    }
    if (reasons.length > 0 && !iscoupon) {
      message.warning(`Discount not applied: ${reasons.join("; ")}`);
    }

    const cartItemsFiltered = billCart.items.filter((i) => i.nextRule === "Y" || i.nextRule === undefined);
    let salePricesTotal = 0;
    for (let i = 0; i < cartItemsFiltered.length; i += 1) {
      salePricesTotal += parseFloat(cartItemsFiltered[i].realPrice);
    }

    billCart.items.forEach((addedToCart, index) => {
      if (addedToCart.discountName === pricingRule.printedName && !addedToCart.isReturn) {
        const sp = parseFloat(addedToCart.realPrice);
        const mrp = parseFloat(sp) * addedToCart.weight;
        const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + addedToCart.taxRate / 100) : (mrp * addedToCart.taxRate) / 100;
        addedToCart.salePrice = sp;
        addedToCart.taxAmount = parseFloat(tax.toFixed(precision));
        addedToCart.nettotal = parseFloat((taxIncludeFlag === "Y" ? mrp : mrp + tax).toFixed(precision));
        addedToCart.discount = 0;
        addedToCart.discountName = "";
        addedToCart.allDiscounts = [];
        // TIC-10890: rule no longer applies — drop its approval so it isn't sent.
        addedToCart.approval = removeRuleApproval(addedToCart.approval, pricingRule.mPricingrulesId);
        enrichLineItemFields(addedToCart, taxIncludeFlag, precision);
        billCart.items[index] = addedToCart;
      }
      // Process children if they exist
      if (addedToCart.children && Array.isArray(addedToCart.children)) {
        addedToCart.children.forEach((child, childIndex) => {
          if (child.discountName === pricingRule.printedName && !child.isReturn) {
            const sp = parseFloat(child.realPrice);
            const mrp = parseFloat(sp) * child.weight;
            const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + child.taxRate / 100) : (mrp * child.taxRate) / 100;
            child.salePrice = sp;
            child.taxAmount = parseFloat(tax.toFixed(precision));
            child.nettotal = parseFloat((taxIncludeFlag === "Y" ? mrp : mrp + tax).toFixed(precision));
            child.discount = 0;
            child.discountName = "";
            child.allDiscounts = [];
            child.approval = removeRuleApproval(child.approval, pricingRule.mPricingrulesId);
            enrichLineItemFields(child, taxIncludeFlag, precision);
            addedToCart.children[childIndex] = child;
          }
        });
        // Update parent's nettotal
        // let childrenNetTotal = addedToCart.children.reduce((sum, child) => sum + (child.nettotal || 0), 0);
        // addedToCart.nettotal = (addedToCart.nettotal || 0) + childrenNetTotal;
      }
    });

    billCart.mPricingruleId = pricingRule.mPricingrulesId;
    billCart.billDiscount = 0;

    const addedToCart = billCart.items;

    let totalTax = 0;
    let totalPrice = 0;
    let totalItemsQty = 0;
    let totalDiscounts = 0;
    for (let i = 0; i < addedToCart.length; i += 1) {
      if (addedToCart[i].isCanceled === "Y") continue;
      totalPrice += addedToCart[i].nettotal;
      totalItemsQty += addedToCart[i].weight;
      totalTax += addedToCart[i].taxAmount;
      totalDiscounts += addedToCart[i].discount;
      if (addedToCart[i].children && Array.isArray(addedToCart[i].children)) {
        addedToCart[i].children.forEach((child) => {
          totalPrice += child.nettotal || 0;
          totalItemsQty += child.weight || 0;
          totalTax += child.taxAmount || 0;
          totalDiscounts += child.discount || 0;
        });
      }
      addedToCart[i].key = i;
    }
    const { roundedTotal: roundOffValue, roundOffAmount: totalRoundOff } = applyCurrencyRounding(totalPrice, getRoundingMethod(tillData), getPrecision(tillData));
    let couponsData = cart?.couponInput?.length > 0 ? [...cart.couponInput] : [];
    if (!couponsData?.some((coupon) => coupon.couponCode === couponInput) && couponInput !== undefined && couponInput !== null) {
      couponsData.push({ couponCode: couponInput, referenceId: uniqReferenceId, mPricingCouponId: mPricingCouponId, mPricingruleId: pricingRule.mPricingrulesId });
    }
    let uniqueArray = cart?.manualDiscountData?.totalLevelDiscount || [];

    let lineLevelDiscounts = cart?.manualDiscountData?.lineLevelDiscount || [];
    let totalLevelDiscounts = cart?.manualDiscountData?.totalLevelDiscount || [];

    let allSequenceNos = [...lineLevelDiscounts.map((item) => item.sequenceNo), ...totalLevelDiscounts.map((item) => item.sequenceNo)];

    let nextSequenceNo = 1;
    while (allSequenceNos.includes(nextSequenceNo)) {
      nextSequenceNo++;
    }

    let duplicateIndex = uniqueArray.findIndex((item) => item.pricingRule === pricingRule.mPricingrulesId);
    const itemData = {
      pricingRule: pricingRule.mPricingrulesId,
      discountName: pricingRule.printedName,
      discountValue: discountInput,
      sequenceNo: duplicateIndex === -1 ? nextSequenceNo : totalLevelDiscounts[duplicateIndex].sequenceNo,
      approver: storedApprover(approver, totalLevelDiscounts[duplicateIndex]),
    };
    if (duplicateIndex === -1) {
      uniqueArray.push(itemData);
    } else {
      uniqueArray[duplicateIndex] = itemData;
    }

    let manualDiscountData = {
      totalLevelDiscount: uniqueArray,
      ...cart.manualDiscountData,
    };
    // BUG-1990: keep subTotal in lockstep with total/tax here too — this branch
    // (rule ineligible / discount removed) still recomputes totals from nettotal,
    // so leaving subTotal stale here reintroduces the same downstream mismatch.
    const recomputedSubTotal = parseFloat(parseFloat(totalPrice - totalTax).toFixed(precision));
    let obj = {
      ...cartObj,
      items: [...addedToCart],
      total: roundOffValue,
      tax: roundTo(totalTax, getPrecision(tillData)),
      subTotal: recomputedSubTotal,
      discount: totalDiscounts,
      totalQty: totalItemsQty,
      roundOff: totalRoundOff,
      iscoupon: iscoupon ? iscoupon : false,
      couponInput: couponsData,
      totalBillDicount: !iscoupon ? null : pricingRule.mPricingrulesId,
      discountType: "TD",
      totalDiscountFlag: true,
      manualDiscountData: manualDiscountData,
      discountApplied: false,
    };
    if (isExchange) {
      useCartStore.getState().setExchangeCart(obj);
    } else if (!iscoupon && !isExchange) {
      setCart(obj);
    }
    return {
      ...cartObj,
      items: [...addedToCart],
      total: roundOffValue,
      tax: roundTo(totalTax, getPrecision(tillData)),
      subTotal: recomputedSubTotal,
      discount: totalDiscounts,
      totalQty: totalItemsQty,
      roundOff: totalRoundOff,
      iscoupon: iscoupon ? iscoupon : false,
      couponInput: couponsData,
      discountType: "TD",
      totalBillDicount: !iscoupon ? null : pricingRule.mPricingrulesId,
      totalDiscountFlag: true,
      manualDiscountData: manualDiscountData,
      discountApplied: false,
    };
  }
};
